import { defineStore } from 'pinia';
import request from '@/api/request';
import { userApi } from '@/api/user';

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null,
    loading: false,
    error: null
  }),

  getters: {
    isLoggedIn: (state) => !!state.user,
    username: (state) => state.user?.username || '',
    email: (state) => state.user?.email || '',
    nickname: (state) => state.user?.nickname || '',
    avatar: (state) => state.user?.avatar || '',
    role: (state) => state.user?.role || 'user',
    roleLevel: (state) => state.user?.role_level || 0,
    userId: (state) => state.user?.id || null
  },

  actions: {
    // 登录
    async login(credentials) {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await request.post('/users/login', credentials);
        
        if (response.code === 200) {
          this.user = response.data;
          localStorage.setItem('isLoggedIn', 'true');
          
          return { success: true };
        } else {
          this.error = response.message;
          return { success: false, message: response.message };
        }
      } catch (error) {
        this.error = error.message;
        return { success: false, message: error.message };
      } finally {
        this.loading = false;
      }
    },

    // 登出
    async logout() {
      try {
        await request.post('/users/logout');
      } catch (error) {
        console.error('Logout error:', error);
      } finally {
        this.user = null;
        localStorage.removeItem('isLoggedIn');
      }
    },

    // 注册
    async register(userData) {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await userApi.register(userData);
        
        if (response.code === 200) {
          return { success: true, data: response.data };
        } else {
          this.error = response.message;
          return { success: false, message: response.message };
        }
      } catch (error) {
        this.error = error.message;
        return { success: false, message: error.message };
      } finally {
        this.loading = false;
      }
    },

    // 获取用户资料
    async fetchProfile() {
      this.loading = true;
      
      try {
        const response = await request.get('/users/profile');
        
        if (response.code === 200) {
          this.user = response.data;
        } else {
          console.error('Fetch profile failed:', response.message);
        }
      } catch (error) {
        console.error('Fetch profile error:', error);
      } finally {
        this.loading = false;
      }
    },

    // 更新资料
    async updateProfile(data) {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await request.put('/users/profile', data);
        
        if (response.code === 200) {
          this.user = { ...this.user, ...data };
          return { success: true };
        } else {
          this.error = response.message;
          return { success: false, message: response.message };
        }
      } catch (error) {
        this.error = error.message;
        return { success: false, message: error.message };
      } finally {
        this.loading = false;
      }
    },

    // 修改密码
    async changePassword(oldPassword, newPassword) {
      this.loading = true;
      this.error = null;
      
      try {
        // TODO: 调用API修改密码
        // const response = await userApi.changePassword({
        //   old_password: oldPassword,
        //   new_password: newPassword
        // });
        
        // 模拟修改成功
        const mockResponse = {
          code: 200,
          message: '密码修改成功'
        };
        
        if (mockResponse.code === 200) {
          return { success: true };
        } else {
          this.error = mockResponse.message;
          return { success: false, message: mockResponse.message };
        }
      } catch (error) {
        this.error = error.message;
        return { success: false, message: error.message };
      } finally {
        this.loading = false;
      }
    },

    // 上传头像
    async uploadAvatar(file) {
      this.loading = true;
      this.error = null;
      
      try {
        // TODO: 调用API上传头像
        // const formData = new FormData();
        // formData.append('avatar', file);
        // const response = await userApi.uploadAvatar(formData);
        
        // 模拟上传成功
        const mockResponse = {
          code: 200,
          data: {
            avatar: '/uploads/avatar.jpg'
          }
        };
        
        if (mockResponse.code === 200) {
          this.user.avatar = mockResponse.data.avatar;
          return { success: true, avatar: mockResponse.data.avatar };
        } else {
          this.error = mockResponse.message;
          return { success: false, message: mockResponse.message };
        }
      } catch (error) {
        this.error = error.message;
        return { success: false, message: error.message };
      } finally {
        this.loading = false;
      }
    },

    async restoreSession() {
      try {
        const response = await request.get('/users/profile');
        if (response.code === 200) {
          this.user = response.data;
          return true;
        } else {
          console.error('Restore session failed:', response.message);
          this.user = null;
          localStorage.removeItem('isLoggedIn');
          return false;
        }
      } catch (error) {
        console.error('Restore session error:', error);
        this.user = null;
        localStorage.removeItem('isLoggedIn');
        return false;
      }
    }
  }
});
